import { useState } from "react";
import Button from "../atoms/button";
import BotOpsConfigSuggestPanel from "./botOpsConfigSuggestPanel";
import { resolveConfigTrust, trustBadgeClass } from "./configTrust";

const CONFIG_TABS = [
    {
        id: "system_prompt",
        label: "System prompt",
        help: "Persona + contrato de saída do turno IA nativo. Entra na montagem de contexto do n8n.",
    },
    {
        id: "guardrails",
        label: "Guardrails",
        help: "Regras que sempre entram no modelo (proibições, quando escalar). Prioridade sobre o pedido do usuário.",
    },
    {
        id: "knowledge",
        label: "Knowledge",
        help: "Base factual (FAQs). No save, vira chunks RAG → CONTEXTO MANAFIX.",
    },
];

function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("pt-BR");
    } catch {
        return String(value);
    }
}

const BotOpsConfigPanel = ({
    token,
    activeVersion,
    versionCreatedAt,
    modelo,
    setModelo,
    temperatura,
    setTemperatura,
    systemPrompt,
    setSystemPrompt,
    guardrails,
    setGuardrails,
    knowledge,
    onAddKnowledge,
    onRemoveKnowledge,
    onKnowledgeChange,
    saving,
    onSave,
    dirty,
    health,
    healthLoading,
    onRefreshHealth,
    onReapply,
    reapplyLoading,
    onApplyPatch,
    acceptedPatchIds,
}) => {
    const [configTab, setConfigTab] = useState("system_prompt");
    const activeTabMeta = CONFIG_TABS.find((t) => t.id === configTab);
    const trust = resolveConfigTrust({ dirty, health, activeVersion });

    const handleAddKnowledge = () => {
        onAddKnowledge();
        setConfigTab("knowledge");
    };

    const mongoId = health?.mongo?.active_version ?? activeVersion ?? "—";
    const runtimeId = health?.runtime?.version_id ?? "—";

    return (
        <>
            <section
                className="rounded-lg border border-[#d59106] p-4 mb-4"
                data-testid="bot-ops-config-trust"
            >
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-2 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-laranjinha font-medium">
                                Estado da configuração
                            </span>
                            <span
                                className={`text-xs px-2 py-0.5 rounded ${trustBadgeClass(
                                    trust.state
                                )}`}
                            >
                                {trust.label}
                            </span>
                        </div>
                        <p className="text-sm text-gray-300">{trust.detail}</p>
                        <div className="text-xs text-gray-400 space-y-1 font-mono">
                            <p>
                                Mongo ativo: {mongoId}
                                {versionCreatedAt ? ` · ${formatDate(versionCreatedAt)}` : ""}
                            </p>
                            <p>Redis aplicado: {runtimeId}</p>
                            {health?.runtime ? (
                                <p>
                                    lens: prompt {health.runtime.prompt_len ?? "—"} · guardrails{" "}
                                    {health.runtime.guardrails_len ?? "—"} · chunks{" "}
                                    {health.runtime.chunk_count ?? "—"}
                                </p>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            click={onRefreshHealth}
                            disabled={healthLoading}
                            classes="border border-gray-400 bg-gray-800 hover:bg-gray-700 px-3 py-1.5 font-medium text-gray-50"
                        >
                            {healthLoading ? "Atualizando…" : "Atualizar status"}
                        </Button>
                        {trust.state === "drift" && !dirty ? (
                            <Button
                                click={onReapply}
                                disabled={reapplyLoading}
                                classes="bg-[#d59106] hover:bg-[#b47c06] px-3 py-1.5 font-medium text-black"
                            >
                                {reapplyLoading ? "Reaplicando…" : "Reaplicar"}
                            </Button>
                        ) : null}
                    </div>
                </div>
            </section>

            <BotOpsConfigSuggestPanel
                token={token}
                onApplyPatch={onApplyPatch}
                acceptedIds={acceptedPatchIds}
            />

            <section className="rounded-lg border border-[#d59106] p-4 mb-4">
                <div className="text-sm text-gray-400 mb-4 space-y-1 rounded-lg bg-gray-900/60 p-3 border border-[#b47c06]/20">
                    <p className="text-xs text-gray-400">
                        Cada Salvar cria uma versão nova imutável e sincroniza Redis (modelo,
                        prompt, guardrails e chunks RAG).
                    </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 mb-4">
                    <label className="block text-sm">
                        <span className="text-laranjinha">Modelo (OpenRouter)</span>
                        <input
                            type="text"
                            value={modelo}
                            onChange={(e) => setModelo(e.target.value)}
                            className="mt-1 w-full text-black p-2 rounded"
                            placeholder="anthropic/claude-sonnet-5"
                            autoComplete="off"
                            spellCheck={false}
                        />
                        <span className="block text-xs text-gray-400 mt-1">
                            Formato fornecedor/modelo. Vazio usa o padrão do sistema.
                        </span>
                    </label>
                    <label className="block text-sm">
                        <span className="text-laranjinha">Temperatura</span>
                        <input
                            type="number"
                            min={0}
                            max={1}
                            step={0.1}
                            value={temperatura}
                            onChange={(e) => setTemperatura(e.target.value)}
                            className="mt-1 w-full text-black p-2 rounded"
                            placeholder="0.3"
                        />
                        <span className="block text-xs text-gray-400 mt-1">
                            Entre 0 e 1. Vazio usa o padrão (0,3).
                        </span>
                    </label>
                </div>

                <div className="flex flex-wrap gap-2 mb-3 border-b border-[#b47c06]/40 pb-2">
                    {CONFIG_TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setConfigTab(tab.id)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                configTab === tab.id
                                    ? "bg-[#d59106] text-black shadow-sm"
                                    : "bg-gray-900/60 text-gray-300 hover:bg-gray-800"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTabMeta ? (
                    <p className="text-xs text-gray-300 mb-3">{activeTabMeta.help}</p>
                ) : null}

                {configTab === "system_prompt" && (
                    <textarea
                        value={systemPrompt}
                        onChange={(e) => setSystemPrompt(e.target.value)}
                        rows={14}
                        className="w-full text-black p-2 rounded"
                        placeholder="System prompt do agente..."
                    />
                )}

                {configTab === "guardrails" && (
                    <textarea
                        value={guardrails}
                        onChange={(e) => setGuardrails(e.target.value)}
                        rows={14}
                        className="w-full text-black p-2 rounded"
                        placeholder="Guardrails e restrições..."
                    />
                )}

                {configTab === "knowledge" && (
                    <div className="space-y-4">
                        {knowledge.length === 0 ? (
                            <p className="text-sm text-gray-300">Nenhum arquivo de knowledge.</p>
                        ) : null}
                        {knowledge.map((file, index) => (
                            <div
                                key={index}
                                className="rounded-lg border border-[#b47c06]/40 bg-gray-900/60 p-3 space-y-2"
                            >
                                <div className="flex flex-wrap items-center gap-2">
                                    <input
                                        type="text"
                                        value={file.filename}
                                        onChange={(e) =>
                                            onKnowledgeChange(index, "filename", e.target.value)
                                        }
                                        placeholder="nome-do-arquivo.md"
                                        className="flex-1 min-w-[200px] text-black p-2 rounded"
                                    />
                                    <Button
                                        click={() => onRemoveKnowledge(index)}
                                        classes="bg-red-600 hover:bg-red-500 px-3 py-1.5 font-medium text-white"
                                    >
                                        Remover
                                    </Button>
                                </div>
                                <textarea
                                    value={file.content}
                                    onChange={(e) =>
                                        onKnowledgeChange(index, "content", e.target.value)
                                    }
                                    rows={8}
                                    className="w-full text-black p-2 rounded"
                                    placeholder="Conteúdo do arquivo..."
                                />
                            </div>
                        ))}
                        <Button
                            click={handleAddKnowledge}
                            classes="bg-blue-300 hover:bg-blue-400 px-3 py-1.5 font-medium text-black"
                        >
                            Adicionar arquivo
                        </Button>
                    </div>
                )}
            </section>

            <div className="text-center">
                <Button
                    click={onSave}
                    disabled={saving}
                    classes="bg-[#d59106] hover:bg-[#b47c06] px-6 py-2 font-medium text-black"
                >
                    {saving ? "Salvando..." : "Salvar"}
                </Button>
            </div>
        </>
    );
};

export default BotOpsConfigPanel;
